import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import {
  WeatherOutlookDayResponse,
  WeatherOutlookResponse,
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-outlook.response';
import { PageHeaderComponent } from '../@commons/@components/app-header/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { pageFromRight } from '../@commons/animations/animations';
import { ErrorStateComponent } from '../@commons/ui-components/error-state/error-state.component';
import { EmptyStateComponent } from '../@commons/ui-components/empty-state/empty-state.component';
import RoutesConstants from '../@commons/constants/routes.constants';
import { VwPluralPipe } from '../@commons/pipes/vw-plural.pipe';
import { appLocale, currentAppLang, formatVwNumber } from '../@commons/pipes/vw-date.pipe';

/**
 * Habillage de chaque verdict météo. Le serveur décide, l'app dessine.
 *
 * `tinted` distingue les journées qui demandent une décision de celles qui se
 * contentent d'exister. Quand les huit lignes sont colorées, plus rien ne
 * ressort : une vague de chaleur au milieu de journées blanches se voit d'un
 * coup d'œil, la même vague au milieu de huit pastels ne se voit pas.
 */
const CONDITION_STYLE: Record<
  string,
  { emoji: string; bg: string; ink: string; bar: string; tinted: boolean }
> = {
  FROST: { emoji: '❄️', bg: '#E7F0FB', ink: '#1B4C7E', bar: '#7EA8D9', tinted: true },
  HOT: { emoji: '🔥', bg: '#FDEEE4', ink: '#8A3B12', bar: '#F09A6A', tinted: true },
  RAIN: { emoji: '🌧️', bg: '#E4EEFB', ink: '#1F4E85', bar: '#6E9BD6', tinted: true },
  DRY: { emoji: '☀️', bg: '#FDF6E0', ink: '#7A5C09', bar: '#E9C75A', tinted: false },
  WIND: { emoji: '💨', bg: '#EDF1F1', ink: '#3E5250', bar: '#9BB0AD', tinted: false },
  MILD: { emoji: '🌤️', bg: '#F1F7F3', ink: '#2C5C46', bar: '#9FC9B2', tinted: false },
};

/** Fond d'une journée qui ne réclame rien : le blanc laisse parler les autres. */
const CALM_ROW_BG = 'var(--vw-surface)';

/** Seuil serveur d'une pluie qui compte. En dessous, le sol reste sec. */
const RAIN_THRESHOLD_MM = 1;

/** Vent qui mérite d'être signalé au jardin. */
const WIND_NOTABLE_KMH = 30;

/** Une série sèche devient un conseil d'arrosage à partir d'ici. */
const DRY_STREAK_NOTABLE_DAYS = 5;

/**
 * D'octobre à avril, le jardinier guette la nuit la plus froide, pas le jour
 * le plus chaud (CJ-06). Mois JavaScript : 0 = janvier.
 */
export function isColdSeason(date: Date): boolean {
  const month = date.getMonth();
  return month >= 9 || month <= 3;
}

interface DayView extends WeatherOutlookDayResponse {
  weekday: string;
  dayNumber: string;
  isToday: boolean;
  isPast: boolean;
}

/** Ce qu'il faut retenir de la semaine, et le geste qui va avec. */
interface Verdict {
  condition: string;
  key: string;
  params: Record<string, string | number>;
}

/**
 * Détail météo sur la fenêtre utile au jardin.
 *
 * La carte d'accueil ne montre que le jour même : elle ne répond pas à la
 * seule question qui compte pour arroser ou protéger — « et après ? ».
 *
 * Aucun seuil n'est calculé ici : c'est le serveur qui qualifie chaque
 * journée, avec les mêmes valeurs que les conseils du jour. Recalculer côté
 * front garantirait une divergence tôt ou tard.
 */
@Component({
  selector: 'vw-weather-detail',
  standalone: true,
  imports: [
    TranslatePipe,
    PageHeaderComponent,
    SkeletonLoaderComponent,
    VwPluralPipe,
    ErrorStateComponent,
    EmptyStateComponent
],
  templateUrl: './weather-detail.component.html',
  animations: [pageFromRight],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: WeatherApi, useClass: WeatherApi }],
})
export class WeatherDetailComponent implements OnInit {
  private readonly weatherApi = inject(WeatherApi);
  private readonly location = inject(Location);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  outlook: WeatherOutlookResponse | null = null;
  days: DayView[] = [];
  isLoading = true;
  failed = false;

  readonly rainThreshold = RAIN_THRESHOLD_MM;
  readonly windNotable = WIND_NOTABLE_KMH;

  /**
   * Tout ce qui se déduit de la fenêtre est calculé une fois, à l'arrivée des
   * données. Le faire dans des accesseurs les rejouerait à chaque cycle de
   * détection pour un résultat invariable.
   */
  scale: { min: number; max: number } = { min: 0, max: 1 };
  verdict: Verdict | null = null;
  peakDay: DayView | null = null;
  /** Saison froide : la troisième tuile montre la nuit la plus froide. */
  coldSeason = false;
  coldestNight: DayView | null = null;
  nextRainInDays: number | null = null;
  /** Pluie du jour quand elle compte (1 mm et plus) : « Aujourd'hui, 1,8 mm ». */
  todayRainMm: number | null = null;

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  /**
   * Charge la météo. Une panne (réseau, serveur) se dit comme une panne, avec
   * « Réessayer » ; une réponse sans jours veut dire que l'adresse ne donne
   * rien, et l'écran mène à l'adresse.
   */
  async load(): Promise<void> {
    this.isLoading = true;
    this.failed = false;
    try {
      this.outlook = await this.weatherApi.getOutlook();
      const todayIso = new Date().toLocaleDateString('en-CA');
      const locale = appLocale(currentAppLang(this.translate));
      this.days = (this.outlook?.days ?? []).map((d) => {
        const date = new Date(d.date + 'T12:00:00');
        return {
          ...d,
          // Dans la langue de l'app : celle du navigateur peut différer.
          weekday: date.toLocaleDateString(locale, { weekday: 'short' }),
          dayNumber: String(date.getDate()),
          isToday: d.date === todayIso,
          isPast: d.date < todayIso,
        };
      });
      this.scale = this.computeScale();
      this.peakDay = this.computePeakDay();
      this.coldSeason = isColdSeason(new Date());
      this.coldestNight = this.computeColdestNight();
      this.nextRainInDays = this.computeNextRainInDays(todayIso);
      const today = this.days.find((d) => d.isToday);
      this.todayRainMm = today && this.countsAsRain(today) ? today.precipitation : null;
      this.verdict = this.computeVerdict();
    } catch {
      this.failed = true;
    } finally {
      this.isLoading = false;
    }
  }

  /** « 17,7 », « 1,8 » : les nombres dans la langue de l'app (B2). */
  num(value: number | null | undefined): string {
    return formatVwNumber(value, currentAppLang(this.translate));
  }

  goBack(): void {
    this.location.back();
  }

  /** État vide : l'adresse se vérifie dans le compte. */
  checkAddress(): void {
    void this.router.navigateByUrl(RoutesConstants.PROFILE);
  }

  style(condition: string) {
    return CONDITION_STYLE[condition] ?? CONDITION_STYLE['MILD'];
  }

  /** Fond de la ligne : teinté seulement si la journée demande une décision. */
  rowBackground(condition: string): string {
    const style = this.style(condition);
    return style.tinted ? style.bg : CALM_ROW_BG;
  }

  /**
   * Cette pluie compte-t-elle pour le sol ?
   *
   * Afficher « 0,36 mm » donne une précision trompeuse : le jardinier lit
   * qu'il va pleuvoir, alors que rien n'atteindra les racines. En dessous du
   * seuil serveur, on ne montre rien plutôt que d'annoncer une pluie qui
   * n'arrose pas.
   */
  countsAsRain(day: DayView): boolean {
    return (day.precipitation ?? 0) >= RAIN_THRESHOLD_MM;
  }

  /**
   * L'échelle de température de la fenêtre, arrondie au degré.
   *
   * Une échelle commune à tous les jours est ce qui rend les barres
   * comparables : c'est elle qui fait voir la canicule monter puis retomber.
   * Une échelle par ligne donnerait huit barres identiques.
   */
  private computeScale(): { min: number; max: number } {
    const mins = this.days
      .map((d) => d.tempMin)
      .filter((t): t is number => t !== null);
    const maxs = this.days
      .map((d) => d.tempMax)
      .filter((t): t is number => t !== null);
    if (!mins.length || !maxs.length) return { min: 0, max: 1 };
    const min = Math.floor(Math.min(...mins));
    const max = Math.ceil(Math.max(...maxs));
    // Une fenêtre plate ne doit pas diviser par zéro.
    return max > min ? { min, max } : { min, max: min + 1 };
  }

  /** Départ de la barre du jour, en % de l'échelle de la fenêtre. */
  bandLeft(day: DayView): number {
    if (day.tempMin === null) return 0;
    const { min, max } = this.scale;
    return ((day.tempMin - min) / (max - min)) * 100;
  }

  /** Largeur de la barre : l'amplitude nuit → après-midi. */
  bandWidth(day: DayView): number {
    if (day.tempMin === null || day.tempMax === null) return 0;
    const { min, max } = this.scale;
    const width = ((day.tempMax - day.tempMin) / (max - min)) * 100;
    // Sous 3 %, la barre disparaît et la ligne semble vide.
    return Math.max(width, 3);
  }

  /**
   * Dans combien de jours la prochaine pluie ? `null` si rien n'est annoncé.
   *
   * Un cumul seul — « 16 mm à venir » — ne dit pas s'il faut arroser ce soir
   * ou attendre. C'est l'échéance qui décide du geste.
   */
  private computeNextRainInDays(todayIso: string): number | null {
    const next = this.days.find(
      (d) => d.date >= todayIso && (d.precipitation ?? 0) >= RAIN_THRESHOLD_MM,
    );
    if (!next) return null;
    const start = new Date(todayIso + 'T12:00:00').getTime();
    const target = new Date(next.date + 'T12:00:00').getTime();
    return Math.round((target - start) / 86_400_000);
  }

  /** Le jour le plus chaud qui reste à venir : c'est celui qu'on prépare. */
  private computePeakDay(): DayView | null {
    const upcoming = this.days.filter((d) => !d.isPast && d.tempMax !== null);
    if (!upcoming.length) return null;
    return upcoming.reduce((hottest, d) =>
      (d.tempMax ?? 0) > (hottest.tempMax ?? 0) ? d : hottest,
    );
  }

  /**
   * La nuit la plus froide qui reste à venir : c'est elle qu'on protège.
   * À égalité, la plus proche, puisqu'il faut agir avant elle.
   */
  private computeColdestNight(): DayView | null {
    const upcoming = this.days.filter((d) => !d.isPast && d.tempMin !== null);
    if (!upcoming.length) return null;
    return upcoming.reduce((coldest, d) =>
      (d.tempMin ?? 0) < (coldest.tempMin ?? 0) ? d : coldest,
    );
  }

  /**
   * Le verdict de la semaine : une phrase, et le geste qu'elle implique.
   *
   * L'écran donnait la matière et laissait conclure. Or la conclusion est
   * précisément ce que l'app sait faire : le gel passe devant la chaleur, la
   * chaleur devant la pluie, et une longue série sèche parle d'elle-même.
   */
  private computeVerdict(): Verdict | null {
    const upcoming = this.days.filter((d) => !d.isPast);
    const label = (d: DayView) => `${d.weekday} ${d.dayNumber}`;

    const frost = upcoming.find((d) => d.condition === 'FROST');
    if (frost) {
      return {
        condition: 'FROST',
        key: 'weather.detail.next.FROST',
        params: { day: label(frost) },
      };
    }

    const hot = upcoming.filter((d) => d.condition === 'HOT');
    if (hot.length > 1) {
      return {
        condition: 'HOT',
        key: 'weather.detail.next.HOT-multi',
        params: { count: hot.length, day: label(hot[hot.length - 1]) },
      };
    }
    if (hot.length === 1) {
      return {
        condition: 'HOT',
        key: 'weather.detail.next.HOT',
        params: { day: label(hot[0]) },
      };
    }

    const rain = upcoming.find((d) => d.condition === 'RAIN');
    // Aujourd'hui et demain ont leur phrase : « Pluie attendue sam. 17 » le
    // samedi 17 ne disait pas qu'il pleuvait déjà (B2).
    if (rain?.isToday) {
      return { condition: 'RAIN', key: 'weather.detail.next.RAIN-today', params: {} };
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (rain && rain.date === tomorrow.toLocaleDateString('en-CA')) {
      return { condition: 'RAIN', key: 'weather.detail.next.RAIN-tomorrow', params: {} };
    }
    if (rain) {
      return {
        condition: 'RAIN',
        key: 'weather.detail.next.RAIN',
        params: { day: label(rain) },
      };
    }

    const streak = this.outlook?.dryStreak ?? 0;
    if (streak >= DRY_STREAK_NOTABLE_DAYS) {
      return {
        condition: 'DRY',
        key: 'weather.detail.next.DRY-streak',
        params: { count: streak },
      };
    }

    return null;
  }
}
